import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Tip from '@components/Tip';
import Title from '@components/Title';
import useTourEditing from '@hooks/useTourEditing';
import PanelBase from '@modules/panel/PanelBase';
import { $GateSandtableGroupSettings } from '@stores/editingMarkerStores';
import type { SandTableGroupSettings } from '@types';
import { useGate } from 'effector-react';
import { SANDTABLE_GROUP_SETTINGS } from '../constants';

interface Props {
  data: SandTableGroupSettings;
  onConfirm: (data: SandTableGroupSettings) => void;
  onRequestClose: () => void;
}

type StyleData = Pick<
  SandTableGroupSettings,
  'bgColor' | 'fontColor' | 'activeFontColor'
>;

const typeItems: DropdownItem<SandTableGroupSettings['type']>[] = [
  { id: 0, name: '纯文字分组' },
  { id: 1, name: '文字图标分组' },
];

export function PanelStyleSetting({ data, onConfirm, onRequestClose }: Props) {
  const { state, setState, defaultState, isChanged } = useTourEditing(
    data,
    SANDTABLE_GROUP_SETTINGS
  );

  useGate($GateSandtableGroupSettings, state);

  const renderColorPicker = (key: keyof StyleData, name: string) => {
    return (
      <ColorPicker
        className="mb-20 last:mb-0"
        colorString={state[key]}
        onChange={(str) =>
          setState((draft) => {
            draft[key] = str;
          })
        }
      >
        <ColorPicker.Header>
          <div>{name}</div>
          <ColorPicker.Reset defaultColor={defaultState[key]!} />
        </ColorPicker.Header>
        <ColorPicker.Bar />
      </ColorPicker>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      title={<Title>分组高级设置</Title>}
      onRequestClose={onRequestClose}
      onConfirm={() => {
        onConfirm(state);
        onRequestClose();
      }}
    >
      <Collapse title="样式设置">
        <Dropdown
          className="mb-20"
          items={typeItems}
          selected={state.type}
          onChange={(val) => {
            setState((draft) => {
              draft.type = val;
            });
          }}
        />
        {renderColorPicker('bgColor', '分组栏背景颜色')}
        {renderColorPicker('fontColor', '默认字体/图标颜色')}
        {renderColorPicker('activeFontColor', '选中字体/图标颜色')}
        <Tip className="mt-20 text-orange">
          注：图标配色仅适用于系统图标，自定义图标不支持
        </Tip>
      </Collapse>
    </PanelBase>
  );
}
